// src/api/api-fetch.ts
import { buildApiUrl } from "./url-route";

type ApiFetchOptions = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
  /** Allow the call to proceed without an Authorization header. */
  allowAnonymous?: boolean;
};

/** Thrown when the API rejects a request with 401. Lets call sites
 *  distinguish auth failure from other ApiErrors without parsing strings. */
export class AuthExpiredError extends Error {
  constructor(public path: string) {
    super("Authentication required");
    this.name = "AuthExpiredError";
  }
}

/** Window-level signal that token is no longer valid. The app root
 *  listens for this and clears state + redirects to login. Listed here
 *  so other API helpers (downloads, raw fetch) can dispatch it too. */
export const AUTH_EXPIRED_EVENT = "auth:expired";

export function notifyAuthExpired(path: string): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { path } }));
  }
}

function buildUrl(path: string) {
  const base = buildApiUrl();
  if (!base) return path;
  if (path.startsWith("http")) return path;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function safeReadText(res: Response) {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

/** Returns the stored token or null. Never returns "" — sending
 *  `Authorization: Bearer ` is the empty-bearer footgun that 401s
 *  silently and pollutes server logs. */
export function getStoredToken(): string | null {
  return (
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    null
  );
}

/**
 * Turn a FastAPI/pydantic error response into something a human can read.
 *
 * Examples handled:
 *  - {"detail": "Invalid username or password"}                            → as-is
 *  - {"detail": [{"msg": "Field required", "loc": ["body","x"]}, ...]}    → "x: Field required"
 *  - {"detail": [{"msg": "Value error, Password must contain ..."}]}      → "Password must contain ..."
 */
function extractErrorMessage(raw: string, status: number): string {
  const generic =
    status >= 500
      ? "Something went wrong on our side. Please try again."
      : `Request failed (${status})`;

  if (!raw) return generic;

  // 5xx bodies are server internals (stack traces, raw exception strings).
  // Never surface them to the client; log the detail for debugging instead.
  if (status >= 500) {
    console.error(`API error ${status}:`, raw);
    return generic;
  }

  let parsed: any = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Non-JSON body (HTML error page, proxy text): not client-readable.
    console.error(`API error ${status} (non-JSON body):`, raw);
    return generic;
  }

  const detail = parsed?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = detail.map((d: any) => {
      let msg = String(d?.msg || "").trim();
      // Strip pydantic v2 "Value error, " prefix
      msg = msg.replace(/^Value error,\s*/i, "");
      // Strip generic "value is not a valid ..." noise prefix
      msg = msg.replace(/^Input should be\s*/i, "");

      // Tack on field name from `loc` if it adds info
      const loc = Array.isArray(d?.loc) ? d.loc : [];
      const field = loc.length > 0 ? String(loc[loc.length - 1]) : "";
      const lower = msg.toLowerCase();
      const useFieldPrefix =
        field &&
        field !== "body" &&
        field !== "__root__" &&
        !lower.includes(field.toLowerCase()) &&
        !lower.includes("password");   // don't double-up "password: Password must..."

      const pretty = field
        .replace(/_/g, " ")
        .replace(/\b\w/g, (s) => s.toUpperCase());

      return useFieldPrefix ? `${pretty}: ${msg}` : msg;
    }).filter(Boolean);

    if (messages.length === 0) return `Validation error (${status})`;
    return messages.join("\n");
  }

  console.error(`API error ${status}:`, raw);
  return generic;
}

export async function apiFetch<T = any>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { allowAnonymous = false, ...fetchOptions } = options;
  const token = getStoredToken();
  const headers: Record<string, string> = { ...(fetchOptions.headers || {}) };

  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const isFormData =
    typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;

  if (!isFormData) {
    if (!headers["Accept"]) headers["Accept"] = "application/json";
    const bodyIsDefined = typeof fetchOptions.body !== "undefined";
    if (bodyIsDefined && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
  }

  const url = buildUrl(path);

  let res: Response;
  try {
    res = await fetch(url, { ...fetchOptions, headers });
  } catch (e: any) {
    // Raw fetch errors (CORS, DNS, connection reset) are developer detail.
    console.error(`API request failed: ${path}`, e);
    throw new Error("Network request failed. Please check your connection and try again.");
  }

  if (res.status === 401 && !allowAnonymous) {
    notifyAuthExpired(path);
    throw new AuthExpiredError(path);
  }

  if (!res.ok) {
    const raw = await safeReadText(res);
    throw new Error(extractErrorMessage(raw, res.status));
  }

  if (res.status === 204) return undefined as any;

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }

  const txt = await safeReadText(res);
  return txt as any;
}